'use client';

import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { formatCurrency } from '@/utils/format';
import { normalizeCardDisplayName } from '@/utils/cardName';
import { ArrowUp, ArrowDown, CreditCard } from 'lucide-react';
import { TotalSpendingTrend } from '@/components/TotalSpendingTrend';

interface CardSpendingData {
  cardId?: string;
  name: string;
  amount: number;
  color: string;
  lastMonthAmount?: number;
  lastMonthAmountEqualDays?: number;
  change?: number;
  plaidItem?: {
    institutionName?: string;
  };
  mask?: string;
  rawName?: string;
}

interface CardSpendingComparisonProps {
  cardSpending: CardSpendingData[];
  loading?: boolean;
  isLoggedIn?: boolean;
  showComparison?: boolean;
  onCardSelect?: (card: CardSpendingData, period?: 'current' | 'previous') => void;
  isPartialMonthScenario?: boolean;
  useEqualDayComparison?: boolean;
  onToggleEqualDayComparison?: () => void;
  currentMonthName?: string;
  previousMonthName?: string;
  // Total Spending Trend props (when card count <= 4)
  showTotalSpendingTrend?: boolean;
  daysSoFar?: number;
  totalThisMonth?: number;
  totalLastMonth?: number;
  totalLastMonthEqualDays?: number;
}

export function CardSpendingComparison({
  cardSpending,
  loading = false,
  isLoggedIn = false,
  showComparison = false,
  onCardSelect,
  isPartialMonthScenario = false,
  useEqualDayComparison = false,
  onToggleEqualDayComparison,
  currentMonthName = 'This Month',
  previousMonthName = 'Last Month',
  showTotalSpendingTrend = false,
  daysSoFar,
  totalThisMonth = 0,
  totalLastMonth = 0,
  totalLastMonthEqualDays = 0
}: CardSpendingComparisonProps) {

  // Helper function to get formatted card display name (same logic as Due Date Cards)
  const getCardDisplayName = (card: CardSpendingData) => {
    const isRobinhood = card.plaidItem?.institutionName?.toLowerCase().includes('robinhood');
    if (isRobinhood) {
      return 'Gold Card';
    }

    const providedName = card.name?.trim();
    if (providedName) {
      return providedName;
    }

    const fallback = card.rawName ?? card.name ?? '';
    return normalizeCardDisplayName(fallback, card.mask);
  };

  // Helper function to get institution name
  const getInstitutionName = (card: CardSpendingData) => {
    return card.plaidItem?.institutionName || '';
  };

  // Use real data from API - no mock data needed
  const enhancedCardData = cardSpending.sort((a, b) => b.amount - a.amount); // Sort by amount in descending order


  const SimpleCard = ({ card }: { card: CardSpendingData }) => {
    const institutionName = getInstitutionName(card);
    const cardDisplayName = getCardDisplayName(card);
    const interactive = typeof onCardSelect === 'function';

    return (
      <div
        className={`group flex items-center justify-between py-3 px-3 rounded-xl transition-all duration-300 ${
          interactive
            ? 'hover:bg-gray-50 hover:scale-[1.02] hover:shadow-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-2 focus:ring-offset-white'
            : ''
        }`}
        role={interactive ? 'button' : undefined}
        tabIndex={interactive ? 0 : undefined}
        onClick={interactive ? () => onCardSelect?.(card) : undefined}
        onKeyDown={(event: ReactKeyboardEvent<HTMLDivElement>) => {
          if (!interactive) return;
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onCardSelect?.(card);
          }
        }}
      >
        <div className="flex items-center">
          <div className={`w-4 h-4 rounded-full ${card.color} mr-3 shadow-sm transition-transform duration-300 group-hover:scale-110`}></div>
          <div className="min-w-0">
            {institutionName && (
              <p className="text-sm text-gray-500 font-semibold leading-tight">
                {institutionName}
              </p>
            )}
            <span className="font-semibold text-gray-900 transition-colors duration-200 group-hover:text-gray-700">
              {cardDisplayName}
            </span>
          </div>
        </div>
        <span className="font-bold text-gray-900 text-lg transition-all duration-300 group-hover:scale-105">{formatCurrency(card.amount)}</span>
      </div>
    );
  };

  const ComparisonCard = ({ card }: { card: CardSpendingData }) => {
    // Choose the appropriate previous month amount based on toggle state
    const previousAmount = (useEqualDayComparison && card.lastMonthAmountEqualDays !== undefined)
      ? card.lastMonthAmountEqualDays
      : (card.lastMonthAmount || 0);

    // Recalculate change percentage with the selected comparison value
    let change = 0;
    if (previousAmount > 0) {
      change = ((card.amount - previousAmount) / previousAmount) * 100;
    } else if (card.amount > 0) {
      change = 100;
    }
    if (!isFinite(change)) {
      change = 0;
    }

    const isIncrease = change >= 0;
    const isZero = Math.abs(change) < 0.1;
    const roundedChange = Math.round(Math.abs(change));
    const interactive = typeof onCardSelect === 'function';

    return (
      <div
        className={`group relative bg-white border border-gray-100 rounded-lg p-2.5 transition-all duration-200 ${
          interactive
            ? 'hover:shadow-sm hover:border-gray-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-2 focus:ring-offset-white'
            : ''
        }`}
        role={interactive ? 'button' : undefined}
        tabIndex={interactive ? 0 : undefined}
        onClick={interactive ? () => onCardSelect?.(card) : undefined}
        onKeyDown={(event: ReactKeyboardEvent<HTMLDivElement>) => {
          if (!interactive) return;
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onCardSelect?.(card);
          }
        }}
      >
        <div className="flex items-center justify-between">
          {/* Left Side - Card Info Only */}
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {/* Card Info */}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1">
                <div className={`w-2.5 h-2.5 rounded-full ${card.color} flex-shrink-0`}></div>
                <h3 className="font-semibold text-gray-900 text-sm leading-tight break-words">
                  {getCardDisplayName(card).split(' ').map((word, index, words) => (
                    <span key={index}>
                      {word}
                      {index < words.length - 1 && index < 2 && <span> </span>}
                      {index === 2 && index < words.length - 1 && <br />}
                      {index > 2 && index < words.length - 1 && <span> </span>}
                    </span>
                  ))}
                </h3>
              </div>
              {getInstitutionName(card) && (
                <p className="text-xs text-gray-500 truncate">
                  {getInstitutionName(card)}
                </p>
              )}
            </div>
          </div>

          {/* Right Side - Amount Comparison + Change Circle */}
          <div className="flex items-center gap-3 text-sm flex-shrink-0">
            <div
              className="text-right cursor-pointer hover:bg-blue-50 rounded px-2 py-1 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                onCardSelect?.(card, 'current');
              }}
              title="Click to view current period transactions"
            >
              <div className="text-xs text-gray-500 uppercase tracking-wide hover:text-blue-600">{currentMonthName}</div>
              <div className="font-bold text-gray-900 transition-all duration-300">{formatCurrency(card.amount)}</div>
            </div>
            <div className="w-px h-6 bg-gray-200" />
            <div
              className="text-right cursor-pointer hover:bg-blue-50 rounded px-2 py-1 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                onCardSelect?.(card, 'previous');
              }}
              title="Click to view previous period transactions"
            >
              <div className="text-xs text-gray-500 uppercase tracking-wide hover:text-blue-600">{previousMonthName}</div>
              <div className="font-medium text-gray-600 transition-all duration-300">{formatCurrency(previousAmount)}</div>
            </div>

            {/* Change Circle - Moved to Far Right */}
            {isZero ? (
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 flex-shrink-0">
                <span className="font-bold text-gray-500 text-xs">0%</span>
              </div>
            ) : (
              <div className={`relative flex items-center justify-center w-8 h-8 rounded-full flex-shrink-0 ${
                isIncrease
                  ? 'bg-gradient-to-br from-red-50 to-red-100 border border-red-200'
                  : 'bg-gradient-to-br from-green-50 to-green-100 border border-green-200'
              }`}>
                <div className="flex flex-col items-center">
                  {isIncrease ? (
                    <ArrowUp className={`h-2.5 w-2.5 text-red-600`} />
                  ) : (
                    <ArrowDown className={`h-2.5 w-2.5 text-green-600`} />
                  )}
                  <span className={`font-bold text-xs leading-none ${
                    isIncrease ? 'text-red-700' : 'text-green-700'
                  }`}>
                    {roundedChange}%
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse bg-gray-100 h-16 rounded-lg" />
        ))}
      </div>
    );
  }

  if (enhancedCardData.length === 0) {
    return (
      <div className="text-center py-8">
        <CreditCard className="h-12 w-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500 font-medium">
          {isLoggedIn ? 'No cards connected yet.' : 'Connect your cards to see spending analytics'}
        </p>
        <p className="text-gray-400 text-sm mt-1">
          Add a credit card to get started
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">

      {enhancedCardData.map((card, index) => (
        <div
          key={card.name}
          className="transition-all duration-300"
          style={{
            transitionDelay: `${index * 50}ms`
          }}
        >
          {showComparison ? (
            <ComparisonCard card={card} />
          ) : (
            <SimpleCard card={card} />
          )}
        </div>
      ))}

      {/* Total Spending Trend (when card count <= 4) */}
      {showTotalSpendingTrend && (
        <TotalSpendingTrend
          totalThisMonth={totalThisMonth}
          totalLastMonth={totalLastMonth}
          totalLastMonthEqualDays={totalLastMonthEqualDays}
          isPartialMonthScenario={isPartialMonthScenario}
          useEqualDayComparison={useEqualDayComparison}
          onToggleEqualDayComparison={onToggleEqualDayComparison}
          currentMonthName={currentMonthName}
          previousMonthName={previousMonthName}
          daysSoFar={daysSoFar}
          isCompactLayout={true}
          onTotalClick={(period) => {
            // Launch transaction modal with all cards selected for the specified date range
            // This will show all transactions across all cards for current or previous period
            onCardSelect?.({
              cardId: 'all',
              name: 'All Cards',
              amount: period === 'current' ? totalThisMonth : (useEqualDayComparison ? totalLastMonthEqualDays : totalLastMonth),
              color: 'bg-blue-500'
            }, period);
          }}
        />
      )}

    </div>
  );
}
